import React, {useMemo, useRef, useState} from 'react';
import _ from "lodash";
import {Form, Input, Select, Spin} from 'antd';
import type {SelectProps} from 'antd/es/select';
import {LabelAndValue} from "../../../create/types/LabelAndValue";
import {apiFetchMapping} from "../../../../containers/QueryBuilder/queryBuilderApi";
import {FormInstance} from "antd/lib/form";
import RemoteSelectAllButton from "./RemoteSelectAllButton";

export interface ValueType {
    key?: string;
    label: React.ReactNode;
    value: string | number
}

interface RemoteSelectProps<ValueType = any> extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
    form: FormInstance
    fieldId: string
    fetchOptions: (search: string) => Promise<ValueType[]>
    debounceTimeout?: number
}

export const DEBOUNCE_TIMEOUT = 800
export const MAX_SIZE = 5000
export const MIN_INPUT_SIZE = 3

const RemoteSelect = ({form, fieldId, fetchOptions, debounceTimeout = DEBOUNCE_TIMEOUT, ...props}: RemoteSelectProps) => {
    const [search, setSearch] = useState<string | undefined>(undefined)
    const [fetching, setFetching] = useState(false)
    const [options, setOptions] = useState<ValueType[]>([])
    const fetchRef = useRef(0)

    const onSelectAll = () => {
        const totalChoices = [...(form.getFieldValue(fieldId) || []), ...options];
        const uniqueChoices = totalChoices.reduce((prev: { label: string, value: number }[], current:{ label: string, value: number }) => {
            const isPresent = prev.find(itemInArray => itemInArray.value === current.value) !== undefined;
            if (isPresent) {
                return prev;
            }
            return [...prev, current];
        }, []);
        form.setFieldValue(fieldId, uniqueChoices)
    }

    const debounceFetcher = useMemo(() => {
        const loadOptions = (value: string) => {
            if (value && value.length >= MIN_INPUT_SIZE) {
                fetchRef.current += 1
                const fetchId = fetchRef.current
                setOptions([])
                setFetching(true)
                setSearch(value)
                fetchOptions(value).then((newOptions) => {
                    if (fetchId !== fetchRef.current) {
                        // for fetch callback order
                        return
                    }
                    setOptions(newOptions)
                    setFetching(false)
                })
            }
        }
        return _.debounce(loadOptions, debounceTimeout)
    }, [fetchOptions, debounceTimeout])

    const searchStatus = () => {
        if (!fetching && search) {
            return <>
                Your search for <b>{search}</b>, found {options.length} item(s).
                {<RemoteSelectAllButton onSelectAll={onSelectAll} />}
            </>
        } else {
            return <> </>
        }
    }

    return <>
        <Form.Item name={`${fieldId}_labels`} hidden={true}>
            <Input/>
        </Form.Item>
        <Form.Item extra={`Please input at least 3 chars to trigger search, for first ${MAX_SIZE} results.`}>
            {searchStatus()}
            <Form.Item name={fieldId} noStyle={true}>
                <Select
                    allowClear={true}
                    maxTagCount={30}
                    mode="multiple"
                    labelInValue
                    filterOption={false}
                    onSearch={debounceFetcher}
                    notFoundContent={fetching ? <Spin size="small"/> : null}
                    {...props}
                    options={options}
                />
            </Form.Item>
        </Form.Item>
    </>
}

export const fetchOptionsList = (fieldCode: string): (searchString: string) => Promise<LabelAndValue[]> => {
    return async (searchString: string) => {
        return apiFetchMapping(fieldCode, searchString, MAX_SIZE)
            .then(i => {
                return i.map(j => ({label: `${j.opt} (${j.code})`, value: j.code}))
            })
    }
}

export default RemoteSelect